import { ComponentType, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Organization } from '@robocorp/robocloud-internal-cds-sdk';
import { useOrganizationByShortnameQuery } from '~/queries';
import { useActions, useParams, useSelector } from '~/hooks';
import ViewLoader from '~/components/ViewLoader';

export const withOrganization =
  (WrappedComponent: ComponentType<{ currentOrganization: Organization }>) => () => {
    const navigate = useNavigate();
    const { organizationShortName } = useParams<'organizationShortName'>();
    const reduxCurrentOrganizationId = useSelector((state) => state.ui.currentOrganizationId);
    const { setCurrentOrganizationId } = useActions(({ uiActions }) => ({
      setCurrentOrganizationId: uiActions.setCurrentOrganizationId,
    }));

    const { data: currentOrganization, isLoading: isLoadingOrganization } =
      useOrganizationByShortnameQuery({ organizationShortName }, { refetchOnWindowFocus: false });

    const currentOrganizationId = currentOrganization?.id;

    const handleError = useCallback(() => {
      window.localStorage.removeItem('currentOrganizationShortName');
      window.sessionStorage.removeItem('currentOrganizationId');
      setCurrentOrganizationId('');

      return navigate('/organization-not-found');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSuccess = useCallback((id: string, shortName: string) => {
      window.localStorage.setItem('currentOrganizationShortName', shortName);
      window.sessionStorage.setItem('currentOrganizationId', id);
      setCurrentOrganizationId(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (isLoadingOrganization) {
        return;
      }

      if (!currentOrganizationId) {
        handleError();
        return;
      }

      handleSuccess(currentOrganizationId, organizationShortName);
    }, [
      handleError,
      handleSuccess,
      isLoadingOrganization,
      currentOrganizationId,
      organizationShortName,
    ]);

    useEffect(() => {
      return () => {
        if (
          !window.location.href.includes('/settings') &&
          !window.location.href.includes('/welcome') &&
          !window.location.href.includes('/subscribe') &&
          !window.location.href.includes('/dashboard')
        ) {
          setCurrentOrganizationId('');
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isCurrentOrganizationIdSynced =
      !!currentOrganizationId && currentOrganizationId === reduxCurrentOrganizationId;
    if (isLoadingOrganization || !isCurrentOrganizationIdSynced) return <ViewLoader />;
    return <WrappedComponent currentOrganization={currentOrganization} />;
  };
